import { LINKS } from "@career/core/constants/links.constant";
import { CandidateService } from "@career/core/services/candidate.service";
import { PortalService } from "@career/core/services/portal.service";
import { QuestionnaireService } from "@career/core/services/questionnaire.service";
import { UserService } from "@career/core/services/user.service";
import { IQuestionnaire } from "@career/questionnaires/models/question";
import { Observable, of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import { ActionDataSourceService } from "../../services/action-data-source.service";
import { ActionProvider } from "../action-data-source.model";
import { ActionStrategy } from "../action-strategy.model";
import { ActionTarget, EActionTargetType } from "../action-target.model";
import {
  IAction,
  EActionType,
  ActionBuilder,
  EActionStatus,
} from "../action.model";

export class FirstSignInActionStrategy extends ActionStrategy {
  constructor(protected dataSourceService: ActionDataSourceService) {
    super(dataSourceService);
  }

  getActionProvider(): ActionProvider {
    return new (class extends ActionProvider {
      constructor(
        private userSvc: UserService,
        private portalService: PortalService,
        private questionnaireService: QuestionnaireService
      ) {
        super();
      }

      setId(action: IAction): string {
        return 'signin';
      }

      populateTarget(action: IAction) {
        action.target = new ActionTarget();
        action.target.name = (action.object as IQuestionnaire).name;
        action.target.type = EActionTargetType.LINK;
        action.target.value = `${LINKS.QUESTIONNAIRE_URL}/${
          (action.object as IQuestionnaire)._id
        }`;
        return action;
      }

      fetch(): Observable<IAction> {
        const actionBuilder = new ActionBuilder();
        return this.userSvc.getUser().pipe(
          filter((user) => user && user._id),
          map((user) => actionBuilder.setUser(user) && user),
          switchMap(() => this.portalService.getPortalConfiguration()),
          switchMap((config) => {
            const questionnaire = config.questionnaire;
            if (!questionnaire) {
              return of(null);
            }
            return this.questionnaireService.findResponse(
              actionBuilder.build().user._id,
              questionnaire
            );
          }),
          map((data) => {
            if (!data) {
              return null;
            }
            actionBuilder
              .setTimestamp(new Date())
              .setStatus(
                data.status === "COMPLETE"
                  ? EActionStatus.COMPLETE
                  : EActionStatus.INCOMPLETE
              )
              .setObject(data.questionnaire)
              .setType(EActionType.PROFILE);
            return actionBuilder.build();
          })
        );
      }
    })(
      this.dataSourceService.get(UserService),
      this.dataSourceService.get(PortalService),
      this.dataSourceService.get(QuestionnaireService)
    );
  }

  getType(): EActionType {
    return EActionType.PROFILE;
  }

  createActions(): Observable<IAction> {
    return this.transformActions(this, []);
  }
}
